import type { DataTypeInDB } from "@apps/server/trpc/routers/crm/crm.utils";
import { type FieldIndentifier, parseFieldIdentifier, parseTableIdentifier, type TableIdentifier } from "@packages/lib";
import { create } from "zustand";
import type { CustomFieldType } from "./types";
import { loadConfigsFromDexie } from "./utils";

export type FieldConfig = {
  fieldIdentifier: FieldIndentifier;
  label: string;
  columnType: DataTypeInDB;
  customType: CustomFieldType | null;
  isNullable: boolean;
  isEditable: boolean;
  options?: {
    value: string;
    label: string;
    disabled: boolean;
  }[];
};

export type Configs = {
  [schema: string]: {
    [table: string]: {
      [field: string]: FieldConfig;
    };
  };
};

type ConfigStore = {
  isReady: boolean;
  configs: Configs;
  loadConfigsFromDexie: () => Promise<void>;
  getConfigByField: (field: FieldIndentifier) => FieldConfig | null;
  getConfigsByTable: (field: TableIdentifier) => Record<FieldIndentifier, FieldConfig> | null;
  getOptionLabel: (field: FieldIndentifier, optionId: string | null | undefined) => string | null;
};

export const useConfigStore = create<ConfigStore>((set, get) => ({
  isReady: false,
  configs: {},
  loadConfigsFromDexie: async () => {
    const configs = await loadConfigsFromDexie();
    set({ configs, isReady: true });
  },
  getConfigByField: (field) => {
    if (!get().isReady) return null;
    const { schemaName, tableName, fieldName } = parseFieldIdentifier(field);
    return get().configs?.[schemaName]?.[tableName]?.[fieldName] || null;
  },
  getConfigsByTable: (table) => {
    if (!get().isReady) return null;
    const { schemaName, tableName } = parseTableIdentifier(table);
    const tableConfigs = get().configs?.[schemaName]?.[tableName];
    if (!tableConfigs) return null;
    return tableConfigs as Record<FieldIndentifier, FieldConfig>;
  },
  getOptionLabel: (field, optionId) => {
    if (!optionId) return null;
    return get().getConfigByField(field)?.options?.find((option) => option.value === optionId)?.label || optionId;
  },
}));
